import { useEffect, useState } from 'react';
import { useOrderNotifications } from '@/hooks/useOrderNotifications';
import { useNotificationPreferences } from '@/hooks/useNotificationPreferences';
import { Button } from '@/components/ui/button';
import { Bell, BellOff, Volume2, VolumeX, Settings } from 'lucide-react';
import { soundNotifications } from '@/utils/soundNotifications';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

export function NotificationManager() {
  const { enabled } = useOrderNotifications();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { preferences, updatePreferences, isUpdating } = useNotificationPreferences();
  const [soundEnabled, setSoundEnabled] = useState(soundNotifications.isEnabled());
  const [browserPermission, setBrowserPermission] = useState<NotificationPermission>('default');
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    if ('Notification' in window) {
      setBrowserPermission(Notification.permission);
    }
  }, []);

  const toggleSound = () => {
    const newState = !soundEnabled;
    soundNotifications.setEnabled(newState);
    setSoundEnabled(newState);
    
    // Update in database
    if (preferences) {
      updatePreferences({ sound_enabled: newState });
    }
  };

  const togglePushNotifications = () => {
    if (preferences) {
      updatePreferences({ push_enabled: !preferences.push_enabled });
    }
  };

  const toggleInAppNotifications = () => {
    if (preferences) {
      updatePreferences({ in_app_enabled: !preferences.in_app_enabled });
    }
  };

  const requestBrowserPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      setBrowserPermission(permission);
    }
  };

  const testNotification = async () => {
    try {
      await soundNotifications.playNewOrderNotification();
      
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('Test Notification', {
          body: 'This is how notifications will look!',
          icon: '/pwa-192x192.png',
          badge: '/pwa-64x64.png',
        });
      }
    } catch (error) {
      console.error('Test notification failed:', error);
    }
  };

  if (!user) return null;

  return (
    <>
      {/* Floating notification toggle button */}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
        <Button
          variant="outline"
          size="icon"
          onClick={toggleSound}
          className="h-12 w-12 rounded-full shadow-lg bg-white hover:bg-gray-50"
          title={soundEnabled ? 'Mute notifications' : 'Enable notification sounds'}
        >
          {soundEnabled ? (
            <Volume2 className="h-5 w-5 text-orange-600" />
          ) : (
            <VolumeX className="h-5 w-5 text-gray-400" />
          )}
        </Button>

        <Button
          variant="outline"
          size="icon"
          onClick={() => setShowSettings(!showSettings)}
          className="h-12 w-12 rounded-full shadow-lg bg-white hover:bg-gray-50"
          title="Notification settings"
        >
          {enabled && browserPermission === 'granted' ? (
            <Bell className="h-5 w-5 text-orange-600" />
          ) : (
            <BellOff className="h-5 w-5 text-gray-400" />
          )}
        </Button>
      </div>

      {/* Settings panel */}
      {showSettings && (
        <div className="fixed bottom-20 right-4 z-50 w-80">
          <Card className="shadow-xl">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Notification Settings</CardTitle>
              <CardDescription className="text-xs">
                Manage how you receive order updates
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                  <span className="text-sm font-medium">Sound Alerts</span>
                </div>
                <Button
                  variant={soundEnabled ? 'default' : 'outline'}
                  size="sm"
                  onClick={toggleSound}
                  className="h-8"
                >
                  {soundEnabled ? 'On' : 'Off'}
                </Button>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {browserPermission === 'granted' ? 
                    <Bell className="h-4 w-4" /> : 
                    <BellOff className="h-4 w-4" />
                  }
                  <span className="text-sm font-medium">Browser Notifications</span>
                </div>
                {browserPermission === 'granted' ? (
                  <span className="text-xs text-green-600 font-medium">Enabled</span>
                ) : browserPermission === 'denied' ? (
                  <span className="text-xs text-red-600 font-medium">Blocked</span>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={requestBrowserPermission}
                    className="h-8"
                  >
                    Enable
                  </Button>
                )}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bell className="h-4 w-4" />
                  <span className="text-sm font-medium">Push Notifications</span>
                </div>
                <Switch
                  checked={preferences?.push_enabled ?? true}
                  onCheckedChange={togglePushNotifications}
                  disabled={isUpdating}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bell className="h-4 w-4" />
                  <span className="text-sm font-medium">In-App Alerts</span>
                </div>
                <Switch
                  checked={preferences?.in_app_enabled ?? true}
                  onCheckedChange={toggleInAppNotifications}
                  disabled={isUpdating}
                />
              </div>

              <div className="pt-2 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={testNotification}
                  className="w-full mb-2"
                  disabled={!soundEnabled && browserPermission !== 'granted'}
                >
                  Test Notification
                </Button>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowSettings(false);
                    navigate('/admin/settings/notifications');
                  }}
                  className="w-full gap-2"
                >
                  <Settings className="h-4 w-4" />
                  Advanced Settings
                </Button>
              </div>

              <div className="text-xs text-muted-foreground pt-2">
                {enabled ? (
                  <p className="text-green-600">✓ You're subscribed to order notifications</p>
                ) : (
                  <p className="text-orange-600">⚠ Notifications inactive</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}
